import React, { useState } from 'react';

import { VALIDATION_REGEX } from '../../../../../shared/components/validation.config';
import { FormElementData } from '../../../../../shared/interfaces/form-element-data.interface';
import { trackEvent } from '../../../../../shared/services/utils/tracking/tracking.service';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import {
  InputText,
  InputTextErrorMode,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import { BrokerUiModel } from '../../../../../shared/ui-models/broker.ui-model';

export interface AddBrokerModalProps {
  config: {
    showModal: boolean;
  };
  eventHandlers: {
    cancelHandler: () => void;
    addBrokerHandler: (broker: BrokerUiModel) => void;
  };
}

/**
 * Only the name is required, unlike a company's.
 *
 * The two brokers that exist today were `channel` enum values with nothing but
 * a name attached, so demanding contact details here would mean inventing them
 * for the very rows this screen exists to manage.
 */
export const AddBrokerModal: React.FC<AddBrokerModalProps> = ({ config, eventHandlers }) => {
  const [brokerName, setBrokerName] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const [contactPersonName, setContactPersonName] = useState<FormElementData<string>>({
    value: '',
    isValid: true
  });
  const [contactPersonEmail, setContactPersonEmail] = useState<FormElementData<string>>({
    value: '',
    isValid: true
  });
  const [contactPersonPhone, setContactPersonPhone] = useState<FormElementData<string>>({
    value: '',
    isValid: true
  });
  const isMobileView = useWindowResizer();

  const isFormValid =
    brokerName.isValid &&
    contactPersonName.isValid &&
    contactPersonEmail.isValid &&
    contactPersonPhone.isValid;

  const handleBrokerSubmit = () => {
    if (!isFormValid) return;

    // Empty optional fields are left off entirely rather than sent as '' -- the
    // API validates the email when it is present, and an empty string is not a
    // valid one.
    eventHandlers.addBrokerHandler({
      name: brokerName.value,
      ...(contactPersonName.value ? { contactPersonName: contactPersonName.value } : {}),
      ...(contactPersonEmail.value ? { contactPersonEmail: contactPersonEmail.value } : {}),
      ...(contactPersonPhone.value ? { contactPersonPhoneNumber: contactPersonPhone.value } : {})
    });
  };

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true }}
      eventHandlers={{
        closeModalHandler: () => {
          eventHandlers.cancelHandler();
        }
      }}
    >
      <div className="flex flex-col gap-3 px-6 py-4">
        <div className="text-2xl font-gilroy-bold font-semibold text-neutral-900">Add Broker</div>
        <div className="text-xl font-medium font-gilroy-medium text-neutral-500">Broker name</div>
        <InputText
          data={{
            defaultValue: '',
            errorMessage: 'Please enter a valid broker name',
            placeholder: 'Enter broker name'
          }}
          config={{
            maxRows: 1,
            autoFocus: true,
            validationRegex: VALIDATION_REGEX.COMPANY_NAME,
            showErrorMode: InputTextErrorMode.instant,
            required: true
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setBrokerName({ value, isValid });
            },
            onEnterPressedHandler: handleBrokerSubmit
          }}
        />
        <div className="text-xl font-medium font-gilroy-medium text-neutral-500">
          Contact person name (optional)
        </div>
        <InputText
          data={{
            defaultValue: '',
            errorMessage: 'Please enter a valid name',
            placeholder: 'Enter contact person name'
          }}
          config={{
            maxRows: 1,
            showErrorMode: InputTextErrorMode.instant
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setContactPersonName({ value, isValid: isValid || !value });
            },
            onEnterPressedHandler: handleBrokerSubmit
          }}
        />
        <div className="text-xl font-medium font-gilroy-medium text-neutral-500">
          Contact person email (optional)
        </div>
        <InputText
          data={{
            defaultValue: '',
            errorMessage: 'Please enter a valid email',
            placeholder: 'Enter contact person email'
          }}
          config={{
            maxRows: 1,
            type: InputTextType.email,
            validationRegex: VALIDATION_REGEX.EMAIL,
            showErrorMode: InputTextErrorMode.instant
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setContactPersonEmail({ value, isValid: isValid || !value });
            },
            onEnterPressedHandler: handleBrokerSubmit
          }}
        />
        <div className="text-xl font-medium font-gilroy-medium text-neutral-500">
          Contact person phone (optional)
        </div>
        <InputText
          data={{
            defaultValue: '',
            errorMessage: 'Please enter a phone number',
            placeholder: 'Enter contact person phone'
          }}
          config={{
            maxRows: 1,
            showErrorMode: InputTextErrorMode.instant
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setContactPersonPhone({ value, isValid: isValid || !value });
            },
            onEnterPressedHandler: handleBrokerSubmit
          }}
        />
      </div>
      <div className="flex flex-col gap-3 px-6 pb-6 sm:flex-row sm:justify-end">
        <Button
          onClick={() => {
            eventHandlers.cancelHandler();
            trackEvent('record_edit_cancelled', { entity: 'broker', method: 'cancel' });
          }}
          tracking={{ event: 'record_edit_cancelled', entity: 'broker', method: 'cancel' }}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          type={ButtonType.primaryInverted}
        >
          Cancel
        </Button>
        <Button
          disabled={!isFormValid}
          onClick={handleBrokerSubmit}
          tracking={{
            event: 'record_saved',
            entity: 'broker',
            action: 'update',
            outcome: 'success'
          }}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
        >
          Add Broker
        </Button>
      </div>
    </Modal>
  );
};
